/**
 * Siteble Runtime Entry Point
 *
 * Built via esbuild into a standalone IIFE bundle (siteble-runtime.js).
 * Loaded on published subdomain pages to hydrate static HTML with real
 * React components — giving 100% fidelity with the editor preview.
 *
 * The published page flow:
 *   1. Static HTML renders instantly (SEO + fast first paint)
 *   2. This runtime loads (~100KB gzipped, CDN-cached)
 *   3. boot() renders real React components via createRoot()
 *   4. Page becomes fully interactive
 */

import React from 'react'
import { createRoot } from 'react-dom/client'

// ============================================
// SHADCN COMPONENTS — Real components (same as editor)
// ============================================
import { Button } from '../../components/ui/button'
import { Card, CardContent, CardHeader, CardFooter, CardTitle, CardDescription } from '../../components/ui/card'
import { Badge } from '../../components/ui/badge'
import { Input } from '../../components/ui/input'
import { Textarea } from '../../components/ui/textarea'
import { Label } from '../../components/ui/label'
import { Separator } from '../../components/ui/separator'
import { Checkbox } from '../../components/ui/checkbox'
import { Switch } from '../../components/ui/switch'
import { Progress } from '../../components/ui/progress'
import { Avatar, AvatarImage, AvatarFallback } from '../../components/ui/avatar'
import { ScrollArea, ScrollBar } from '../../components/ui/scroll-area'
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from '../../components/ui/accordion'
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger,
  SheetClose, SheetDescription, SheetFooter,
} from '../../components/ui/sheet'
import {
  Dialog, DialogTrigger, DialogContent, DialogHeader, DialogTitle,
  DialogDescription, DialogFooter, DialogClose,
} from '../../components/ui/dialog'
import {
  Tabs, TabsList, TabsTrigger, TabsContent,
} from '../../components/ui/tabs'
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from '../../components/ui/tooltip'
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
  SelectGroup, SelectLabel,
} from '../../components/ui/select'
import { RadioGroup, RadioGroupItem } from '../../components/ui/radio-group'
import {
  Collapsible, CollapsibleContent, CollapsibleTrigger,
} from '../../components/ui/collapsible'

// ============================================
// CONTACT FORM — Real interactive form
// ============================================
import { ContactForm } from '../../components/forms/ContactForm'

// ============================================
// LUCIDE ICONS — Top ~60 most commonly used in landing pages
// Named imports only (not import *) to keep bundle small
// ============================================
import {
  ArrowRight, ArrowLeft, ArrowDown, ArrowUp,
  Check, CheckCircle, CheckCircle2, ChevronDown, ChevronUp, ChevronRight, ChevronLeft,
  Star, Heart, Phone, Mail, MapPin, Clock, Calendar,
  Users, User, Shield, Zap, Award, Globe, Target, Sparkles,
  Menu, X, Search, Plus, Minus, ExternalLink,
  Facebook, Instagram, Twitter, Linkedin, Youtube,
  Send, MessageCircle, MessageSquare, ThumbsUp, Eye,
  Download, Upload, Settings, Home, Building, Building2, Briefcase,
  Camera, Image, Play, Pause, Quote,
  Lock, Unlock, CreditCard, Gift, Rocket, Lightbulb,
  CircleCheck, CircleDot, Info, AlertCircle, HelpCircle,
  Headphones, Wrench, Truck, Package, ShoppingCart,
  Crown, Trophy, Gem, BadgeCheck, Palette, Paintbrush,
  Scissors, Coffee, Utensils, Wine, Leaf, Flower2,
  Wifi, Monitor, Smartphone, Laptop,
  FileText, PenLine, BookOpen, GraduationCap,
  Activity, TrendingUp, BarChart2, BarChart3, PieChart,
  Navigation, Compass, Map,
  Timer, CalendarDays, AlarmClock,
  Handshake, PartyPopper, Flame, Verified,
  CircleArrowRight, ArrowUpRight, MoveRight,
  AtSign, Hash, Link, Copy,
} from 'lucide-react'

const ICONS: Record<string, React.ComponentType<any>> = {
  ArrowRight, ArrowLeft, ArrowDown, ArrowUp,
  Check, CheckCircle, CheckCircle2, ChevronDown, ChevronUp, ChevronRight, ChevronLeft,
  Star, Heart, Phone, Mail, MapPin, Clock, Calendar,
  Users, User, Shield, Zap, Award, Globe, Target, Sparkles,
  Menu, X, Search, Plus, Minus, ExternalLink,
  Facebook, Instagram, Twitter, Linkedin, Youtube,
  Send, MessageCircle, MessageSquare, ThumbsUp, Eye,
  Download, Upload, Settings, Home, Building, Building2, Briefcase,
  Camera, Image, Play, Pause, Quote,
  Lock, Unlock, CreditCard, Gift, Rocket, Lightbulb,
  CircleCheck, CircleDot, Info, AlertCircle, HelpCircle,
  Headphones, Wrench, Truck, Package, ShoppingCart,
  Crown, Trophy, Gem, BadgeCheck, Palette, Paintbrush,
  Scissors, Coffee, Utensils, Wine, Leaf, Flower2,
  Wifi, Monitor, Smartphone, Laptop,
  FileText, PenLine, BookOpen, GraduationCap,
  Activity, TrendingUp, BarChart2, BarChart3, PieChart,
  Navigation, Compass, Map,
  Timer, CalendarDays, AlarmClock,
  Handshake, PartyPopper, Flame, Verified,
  CircleArrowRight, ArrowUpRight, MoveRight,
  AtSign, Hash, Link, Copy,
}

// ============================================
// FALLBACK ICON — Renders nothing for unknown icon names
// ============================================
const FallbackIcon: React.FC<{ name: string }> = ({ name }) => {
  console.warn(`[siteble-runtime] Unknown icon "${name}"`)
  return null
}

// ============================================
// SCOPE — Everything AI-generated code can reference
// Icons go first so shadcn component names override collisions
// ============================================
const SCOPE: Record<string, unknown> = {
  // Lucide icons FIRST
  ...ICONS,

  // React
  React,
  useState: React.useState,
  useEffect: React.useEffect,
  useRef: React.useRef,
  useCallback: React.useCallback,
  useMemo: React.useMemo,
  useId: React.useId,
  Fragment: React.Fragment,

  // Button, Card
  Button,
  Card, CardContent, CardHeader, CardFooter, CardTitle, CardDescription,

  // Forms
  Input, Textarea, Label, Checkbox, Switch,
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue, SelectGroup, SelectLabel,
  RadioGroup, RadioGroupItem,

  // Display
  Badge, Progress, Separator, Avatar, AvatarImage, AvatarFallback,

  // Layout
  ScrollArea, ScrollBar,
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
  Tabs, TabsList, TabsTrigger, TabsContent,
  Collapsible, CollapsibleContent, CollapsibleTrigger,

  // Overlays — each section is wrapped in Dialog + Sheet providers by boot()
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger, SheetClose, SheetDescription, SheetFooter,
  Dialog, DialogTrigger, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogClose,
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,

  // Forms
  ContactForm,

  // siteId — set by boot script before factories execute
  siteId: '',
}

// Wrap SCOPE in a Proxy: unknown PascalCase names get a fallback icon
// instead of undefined (which would crash React rendering)
const SCOPED = new Proxy(SCOPE, {
  get(target, prop: string) {
    if (prop in target) return target[prop]
    // PascalCase heuristic: starts with uppercase
    if (/^[A-Z]/.test(prop)) {
      const icon = (props: any) => React.createElement(FallbackIcon, { name: prop, ...props })
      icon.displayName = prop
      return icon
    }
    return undefined
  },
})

// ============================================
// BOOT — Called by the inline script on the published page
// ============================================

interface BootOptions {
  /** Pre-built React component functions (from factory closures) */
  sections: React.ComponentType[]
  /** Site ID for ContactForm */
  siteId?: string
}

function boot(options: BootOptions) {
  const container = document.getElementById('__siteble_root')
  if (!container) {
    console.error('[siteble-runtime] #__siteble_root not found')
    return
  }

  const { sections, siteId } = options
  console.log(`[siteble-runtime] Booting with ${sections.length} sections`)

  // Build the React tree — each section wrapped in Dialog + Sheet providers
  // (same as DynamicRenderer in the editor)
  const elements = sections.map((Component, i) => (
    <ErrorBoundary key={i} index={i}>
      <Dialog>
        <Sheet>
          <Component />
        </Sheet>
      </Dialog>
    </ErrorBoundary>
  ))

  // Replace static HTML with live React
  const root = createRoot(container)
  root.render(<>{elements}</>)
}

// ============================================
// ERROR BOUNDARY — Graceful per-section error handling
// ============================================
class ErrorBoundary extends React.Component<
  { children: React.ReactNode; index: number },
  { error: Error | null }
> {
  state: { error: Error | null } = { error: null }

  static getDerivedStateFromError(error: Error) {
    return { error }
  }

  componentDidCatch(error: Error) {
    console.error(`[siteble-runtime] Section ${this.props.index} error:`, error)
  }

  render() {
    if (this.state.error) {
      // On error, keep the static HTML visible (don't blank the section)
      return null
    }
    return this.props.children
  }
}

// ============================================
// EXPOSE ON WINDOW
// ============================================
;(window as any).__SITEBLE__ = {
  boot,
  SCOPE: SCOPED,
  React,
  createRoot,
}


